import { useEffect, useState } from "react";
import { Search, UserPlus, X } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BADGE = { member: "bg-[#1e3a5f] text-white", guest: "bg-amber-100 text-amber-700", unregistered: "bg-slate-200 text-slate-600" };

export function BuyerBadge({ type }) {
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${BADGE[type]}`}>{type}</span>;
}

export default function BuyerPicker({ authAxios, buyer, onChange }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (buyer || q.trim().length < 2) { setRows([]); return; }
    const t = setTimeout(() => {
      authAxios.get("/shop-admin/buyers/search", { params: { q: q.trim() } }).then(r => setRows(r.data)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, buyer]); // eslint-disable-line react-hooks/exhaustive-deps

  if (buyer) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl border bg-slate-50" data-testid="issue-buyer-selected">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#1e3a5f] truncate flex items-center gap-2">{buyer.name} <BuyerBadge type={buyer.type} /></p>
          <p className="text-xs text-slate-500 truncate">{buyer.email}{buyer.id ? ` · ${buyer.id}` : ""}</p>
        </div>
        <button onClick={() => { onChange(null); setQ(""); setNewName(""); }} className="p-1.5 rounded-lg hover:bg-slate-200" aria-label="Change buyer" data-testid="issue-buyer-clear"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  const typedEmail = EMAIL_RE.test(q.trim()) ? q.trim().toLowerCase() : null;
  const exact = typedEmail && rows.some(r => (r.email || "").toLowerCase() === typedEmail);
  return (
    <div className="relative" data-testid="issue-buyer-picker">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search buyer by email, name or ID"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm" data-testid="issue-email-input" />
      </div>
      {(rows.length > 0 || (q.trim().length >= 2)) && (
        <div className="mt-1 rounded-xl border bg-white shadow-lg overflow-hidden" data-testid="issue-buyer-results">
          {rows.map(r => (
            <button key={r.id || r.email} onClick={() => onChange(r)} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2 border-b last:border-b-0" data-testid={`issue-buyer-option-${r.id || r.email}`}>
              <span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{r.name || r.email}</span><span className="block text-xs text-slate-500 truncate">{r.email}{r.id ? ` · ${r.id}` : " · previous shop buyer"}</span></span>
              <BuyerBadge type={r.type} />
            </button>
          ))}
          {rows.length === 0 && !typedEmail && <p className="px-3 py-2.5 text-xs text-slate-400" data-testid="issue-buyer-none">No member or guest found — type the full email to add a new buyer.</p>}
          {typedEmail && !exact && (
            <div className="p-3 bg-slate-50 space-y-2" data-testid="issue-buyer-new">
              <p className="text-xs text-slate-600 flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> New buyer (no account) · <strong>{typedEmail}</strong></p>
              <div className="flex gap-2">
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Buyer name" className="flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm" data-testid="issue-new-name-input" />
                <button disabled={!newName.trim()} onClick={() => onChange({ type: "unregistered", email: typedEmail, name: newName.trim() })}
                  className="px-3 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-semibold disabled:opacity-40" data-testid="issue-new-buyer-btn">Use</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
